import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useFilteredStores } from 'maps/hooks/useFilteredStores/useFilteredStores'
import { useMapDispatch } from 'maps/hooks/useMapDispatch/useMapDispatch'
import { useMapState } from 'maps/hooks/useMapState/useMapState'
import { useStoredLocation } from 'maps/hooks/useStoredLocation/useStoredLocation'
import { geocode } from 'maps/services/client/geocode/geocode'
import type { Location } from 'maps/types'
import { sendSearchStoreNearbyEvent } from 'maps/utils/analytics/events'
import { getCurrentPosition } from 'maps/utils/geolocation/geolocation'
import { isDroppointType, isRfidStoresType } from 'maps/utils/mapTypes/mapTypes'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useEffect, useState } from 'react'

export const useGeolocation = (isModal = false) => {
	const { t } = useLabels()
	const { type } = useMapState()
	const dispatch = useMapDispatch()
	const {
		country: { countryISO, languageISO },
	} = useMasterData()
	const { setLatestLocation } = useStoredLocation(false)
	const { handleFilteredStores } = useFilteredStores()

	const [location, setLocation] = useState<Location | null>(null)
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [geolocationErrorMessage, setGeolocationErrorMessage] = useState<
		string | null
	>(null)
	const [isGeoLocationEnabled, setIsGeoLocationEnabled] =
		useState<boolean>(false)

	const searchInput = t('storeLocator.searchBox.nearMe.input')
	const errorMessage = t('storeLocator.searchBox.nearMe.disabled.placeholder')

	const handleGetCurrentPosition = async (): Promise<void> => {
		dispatch({ loading: true, searchInput })
		try {
			const coordinates = await getCurrentPosition()
			let currentLocation: Location = { coordinates }

			if (isDroppointType(type) || isRfidStoresType(type)) {
				const { addressInfo } =
					(await geocode({
						coordinates,
						countryISO,
						languageISO,
					})) ?? {}
				currentLocation = { ...currentLocation, addressInfo }
			}
			handleFilteredStores(currentLocation)
			setLatestLocation(searchInput, currentLocation)
			sendSearchStoreNearbyEvent()
		} catch (error) {
			setGeolocationErrorMessage(errorMessage)
			setIsGeoLocationEnabled(false)
			dispatch({ loading: false })
			throw new Error(
				`[Maps] Something went wrong retrieving current position, ${error}`
			)
		}
	}

	useEffect(() => {
		if (!isModal) {
			setIsGeoLocationEnabled(true)
			setIsLoading(false)
			return
		}

		navigator.geolocation.getCurrentPosition(
			(position) => {
				const { latitude, longitude } = position.coords

				const currentPosition = {
					coordinates: { lat: latitude, lng: longitude },
					addressInfo: {
						cityName: '',
						address: '',
						postalCode: '',
					},
				}

				setIsGeoLocationEnabled(true)
				setLocation(currentPosition)
				setIsLoading(false)
			},
			() => {
				setIsGeoLocationEnabled(false)
				setGeolocationErrorMessage(errorMessage)
				setIsLoading(false)
			}
		)
	}, [isModal, errorMessage])

	return {
		location,
		isGeoLocationEnabled,
		geolocationErrorMessage,
		isLoading,
		handleGetCurrentPosition,
	}
}
